import type { ExternalLogger } from '@cld/unicorn-api';
import type { Action } from './actions';
import type { State } from './state';

const handleDataChangeNotificationReceived = (
  state: Extract<State, { applicationStatus: 'ready' }>,
  action: Extract<
    Action,
    {
      type: 'dataChangeNotificationReceived';
    }
  >
): State => {
  // FIXME: eslint somehow didn't see non-exhaustive switch case here
  switch (action.payload.affectedData) {
    case 'account':
      if (state.accountFetchState.status === 'idle') {
        return { ...state, accountFetchState: { status: 'pending', requestData: {} } };
      }
      break;

    case 'plan':
      if (state.planFetchState.status === 'idle') {
        return { ...state, planFetchState: { status: 'pending', requestData: {} } };
      }
      break;

    case 'user':
      if (state.userFetchState.status === 'idle') {
        return { ...state, userFetchState: { status: 'pending', requestData: {} } };
      }
      break;

    case 'capabilities':
      if (state.capabilitiesFetchState.status === 'idle') {
        return { ...state, capabilitiesFetchState: { status: 'pending', requestData: {} } };
      }
      break;

    case 'productEnvironment':
      if (state.productEnvironmentFetchState.status === 'idle') {
        return { ...state, productEnvironmentFetchState: { status: 'pending', requestData: { externalId: state.productEnvironment.externalId } } };
      }
      break;

    case 'permissions':
      if (state.permissionsFetchState.status === 'idle') {
        return { ...state, permissionsFetchState: { status: 'pending', requestData: {} } };
      }
      break;

    case 'deprecatedData':
      if (state.deprecatedDataFetchState.status === 'idle') {
        return { ...state, deprecatedDataFetchState: { status: 'pending', requestData: {} } };
      }
      break;
  }

  return state;
};

// FIXME: consider combining separate reducers for each application status once state slices are introduced
export const createReducer =
  ({ unicornExternalLogger }: { unicornExternalLogger: ExternalLogger }) =>
  (state: State, action: Action): State => {
    if (state.applicationStatus === 'startup') {
      if (action.type === 'applicationStartupActiveProductDetected') {
        return {
          ...state,
          startupStep: {
            name: 'pickingInitialProductEnvironment',
            activeProductName: action.payload.activeProductName,
          },
        };
      }

      if (action.type === 'applicationStartupInitialProductEnvironmentSelected') {
        return {
          ...state,
          startupStep: {
            name: 'fetchingInitialData',
            activeProductName: action.payload.activeProductName,
            productEnvironment: action.payload.productEnvironment,
          },
        };
      }

      if (action.type === 'applicationStartupFinished') {
        return {
          ...state,
          applicationStatus: 'ready',
          account: action.payload.account,
          capabilities: action.payload.capabilities,
          plan: action.payload.plan,
          permissions: action.payload.permissions,
          productEnvironment: action.payload.productEnvironment,
          user: action.payload.user,
          deprecatedData: action.payload.deprecatedData,
          assetVisibility: action.payload.assetVisibility,
          theme: action.payload.theme,
          activeProductName: action.payload.activeProductName,
          isImpersonationActive: action.payload.isImpersonationActive,
          mainLogoUrl: action.payload.mainLogoUrl,
          productEnvironmentChangeState: { status: 'idle' },
          productEnvironmentFetchState: { status: 'idle' },
          accountFetchState: { status: 'idle' },
          userFetchState: { status: 'idle' },
          capabilitiesFetchState: { status: 'idle' },
          deprecatedDataFetchState: { status: 'idle' },
          permissionsFetchState: { status: 'idle' },
          planFetchState: { status: 'idle' },
          postGdprLogState: { status: 'idle' },
        };
      }

      if (action.type === 'applicationStartupFailed') {
        return {
          ...state,
          applicationStatus: 'startupError',
          error: action.payload.error,
        };
      }
    } else if (state.applicationStatus === 'ready') {
      switch (action.type) {
        case 'deprecatedDataFetchQueued':
          return {
            ...state,
            deprecatedDataFetchState: { status: 'pending', requestData: {} },
          };
        case 'deprecatedDataFetchFinished':
          return {
            ...state,
            deprecatedDataFetchState: { status: 'idle' },
            ...(action.payload.kind === 'success' ? { deprecatedData: action.payload.data } : {}),
          };

        case 'accountFetchQueued':
          return {
            ...state,
            accountFetchState: { status: 'pending', requestData: {} },
          };
        case 'accountFetchFinished':
          return {
            ...state,
            accountFetchState: { status: 'idle' },
            ...(action.payload.kind === 'success' ? { account: action.payload.data } : {}),
          };

        case 'planFetchQueued':
          return {
            ...state,
            planFetchState: { status: 'pending', requestData: {} },
          };
        case 'planFetchFinished':
          return {
            ...state,
            planFetchState: { status: 'idle' },
            ...(action.payload.kind === 'success' ? { plan: action.payload.data } : {}),
          };

        case 'capabilitiesFetchQueued':
          return {
            ...state,
            capabilitiesFetchState: { status: 'pending', requestData: {} },
          };
        case 'capabilitiesFetchFinished':
          return {
            ...state,
            capabilitiesFetchState: { status: 'idle' },
            ...(action.payload.kind === 'success' ? { capabilities: action.payload.data } : {}),
          };

        case 'permissionsFetchQueued':
          return {
            ...state,
            permissionsFetchState: { status: 'pending', requestData: {} },
          };
        case 'permissionsFetchFinished':
          return {
            ...state,
            permissionsFetchState: { status: 'idle' },
            ...(action.payload.kind === 'success' ? { permissions: action.payload.data } : {}),
          };

        case 'userFetchQueued':
          return {
            ...state,
            userFetchState: { status: 'pending', requestData: {} },
          };
        case 'userFetchFinished':
          return {
            ...state,
            userFetchState: { status: 'idle' },
            ...(action.payload.kind === 'success'
              ? {
                  user: action.payload.data,
                  deprecatedData: {
                    ...state.deprecatedData,
                    intercomHash: action.payload.data.intercomHash,
                  },
                }
              : {}),
          };

        case 'productEnvironmentFetchQueued':
          return {
            ...state,
            productEnvironmentFetchState: { status: 'pending', requestData: { externalId: action.payload.externalId } },
          };
        case 'productEnvironmentFetchFinished':
          return {
            ...state,
            productEnvironmentFetchState: { status: 'idle' },
            ...(action.payload.kind === 'success' ? { productEnvironment: action.payload.data } : {}),
          };

        case 'productEnvironmentChangeQueued':
          return {
            ...state,
            productEnvironmentChangeState: { status: 'pending', newExternalId: action.payload.newExternalId },
          };
        case 'productEnvironmentChangeFinished':
          return {
            ...state,
            productEnvironmentChangeState: { status: 'idle' },
            ...(action.payload.kind === 'success'
              ? {
                  productEnvironment: action.payload.productEnvironment,
                  capabilities: action.payload.capabilities,
                  deprecatedData: action.payload.deprecatedData,
                }
              : undefined),
          };

        case 'dataChangeNotificationReceived':
          return handleDataChangeNotificationReceived(state, action);

        case 'disableAssetVisibility':
          return {
            ...state,
            assetVisibility: 'notVisible',
            postGdprLogState: {
              status: 'idle',
            },
          };

        case 'postGdprLogQueued':
          return {
            ...state,
            postGdprLogState: {
              status: 'pending',
              requestData: {
                details: action.payload.details,
                justification: action.payload.justification,
              },
            },
          };
        case 'postGdprLogFailed':
          return {
            ...state,
            postGdprLogState: { status: 'idle' },
          };
        case 'postGdprLogSuccess':
          return {
            ...state,
            assetVisibility: 'visible',
            postGdprLogState: { status: 'idle' },
          };
      }
    }

    unicornExternalLogger.warning('Dispatched state action that could not be handled in the current application state', {
      applicationStatus: state.applicationStatus,
      action,
    });

    return state;
  };
